'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import PropTypes from 'prop-types';

import ButtonCircle from 'components/ui/ButtonCircle';
import Img from 'components/ui/Img';
import RichText, { BLOCKS } from 'components/ui/RichText';
import Text from 'components/ui/Text';

import { getColor } from 'utils';
import { addProtocol } from 'utils/url';

import styles from './CardFlip.module.scss';

const CardFlip = ({
    className,
    imagePortrait,
    imageLandscape,
    frontText,
    frontTextColor,
    backText,
    backBackgroundColor,
}) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const [isFlipping, setIsFlipping] = useState(false);
    const flipTimerRef = useRef(null);
    const frontBtnRef = useRef(null);
    const backBtnRef = useRef(null);
    const isFirstRender = useRef(true);
    const wasKeyboard = useRef(false);

    useEffect(() => {
        return () => {
            clearTimeout(flipTimerRef.current);
        };
    }, []);

    // Prevent flickering of the faux button's colors while flipping
    const handleFlip = toFlipped => {
        if (isFlipping) return;
        setIsFlipped(toFlipped);
        setIsFlipping(true);
        clearTimeout(flipTimerRef.current);
        flipTimerRef.current = setTimeout(() => {
            setIsFlipping(false);
        }, 600);
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (wasKeyboard.current) {
            wasKeyboard.current = false;
            if (isFlipped) {
                backBtnRef.current?.focus();
            } else {
                frontBtnRef.current?.focus();
            }
        }
    }, [isFlipped]);

    const fallbackSrc = imageLandscape?.url || imagePortrait?.url;
    const desktopSrc = imagePortrait?.url || imageLandscape?.url;

    const customSources = desktopSrc
        ? [
              {
                  breakpoint: 1024,
                  imageWidth: 1024,
                  src: addProtocol(desktopSrc),
              },
          ]
        : [];

    const cardStyles = {
        ...(frontTextColor && {
            '--card-front-text-color': getColor(frontTextColor),
        }),
        ...(backBackgroundColor && {
            '--card-back-background-color': getColor(backBackgroundColor),
        }),
    };

    return (
        <div
            className={cx(
                styles.card,
                isFlipping && styles.isFlipping,
                className
            )}
            style={cardStyles}
        >
            <div className={cx(styles.inner, isFlipped && styles.isFlipped)}>
                <div className={styles.front}>
                    <button
                        ref={frontBtnRef}
                        type="button"
                        className={styles.cardFaceBtn}
                        onClick={() => handleFlip(true)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                wasKeyboard.current = true;
                            }
                        }}
                        aria-label="Show details"
                        aria-expanded={isFlipped}
                        disabled={isFlipped}
                    >
                        {fallbackSrc && (
                            <Img
                                className={styles.image}
                                src={addProtocol(fallbackSrc)}
                                alt=""
                                aria-hidden="true"
                                fallbackImageWidth={768}
                                customSources={customSources}
                            />
                        )}
                        {frontText && (
                            <Text
                                as="span"
                                baseTheme="headingSmall"
                                themes={{ medium: 'headingLarge' }}
                                className={styles.frontText}
                            >
                                {frontText}
                            </Text>
                        )}
                        <ButtonCircle
                            as="span"
                            theme="small-fill-white"
                            iconType="add"
                            className={styles.toggleBtn}
                            iconClassName={styles.toggleBtnIcon}
                            aria-hidden="true"
                        />
                    </button>
                </div>
                <div className={styles.back}>
                    <button
                        ref={backBtnRef}
                        type="button"
                        className={styles.cardFaceBtn}
                        onClick={() => handleFlip(false)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                wasKeyboard.current = true;
                            }
                        }}
                        aria-label="Close details"
                        aria-expanded={isFlipped}
                        disabled={!isFlipped}
                    >
                        {backText?.json && (
                            <span className={styles.backContent}>
                                <RichText
                                    richText={backText}
                                    overrides={{
                                        renderNode: {
                                            [BLOCKS.PARAGRAPH]: (
                                                _node,
                                                children
                                            ) => (
                                                <Text
                                                    as="span"
                                                    baseTheme="bodySmall"
                                                    themes={{
                                                        medium: 'bodyLarge',
                                                    }}
                                                    className={
                                                        styles.backParagraph
                                                    }
                                                >
                                                    {children}
                                                </Text>
                                            ),
                                        },
                                    }}
                                />
                            </span>
                        )}
                        <ButtonCircle
                            as="span"
                            theme="small-fill-blue"
                            iconType="add"
                            className={styles.toggleBtn}
                            iconClassName={styles.toggleBtnIcon}
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </div>
        </div>
    );
};

CardFlip.propTypes = {
    className: PropTypes.string,
    imagePortrait: PropTypes.object,
    imageLandscape: PropTypes.object,
    frontText: PropTypes.string,
    frontTextColor: PropTypes.string,
    backText: PropTypes.object,
    backBackgroundColor: PropTypes.string,
};

export default CardFlip;
